import React from 'react';
import { ReportBaseProps } from '../WeeklyReport';
import {
  AdvisorComment as AdvisorCommentInternal,
  AdvisorCommentProps as AdvisorCommentProps_,
  CalcAvatarIconType as CalcAvatarIconType_,
  GetCommentContent,
  GetCommentTitle,
} from '../../components/AdvisorComment';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import {
  addPercentAndDeltaVerboseFormat,
  commaFormatPercentage,
} from 'components/features/new-report/utils/formatter';
import { AssetHistoryItemsProps } from 'components/features/home/assetStuffing/AssetStuffing';

export type AdvisorCommentProps = AdvisorCommentProps_;

type CalcAvatarIconType = CalcAvatarIconType_;

const getCommentContent: GetCommentContent = (
  avatarIconType,
  changeTotalAssetAmount,
  _
) => {
  switch (avatarIconType) {
    case 'veryBad':
    case 'bad':
      return `先週末日より資産が${changeTotalAssetAmount}%減っています。口座別資産変動で資産の変動の内訳を確認しましょう。`;
    case 'average':
      return `先週末日と資産額は変わりありません。`;
    case 'good':
    case 'veryGood':
      return `先週末日より資産が${changeTotalAssetAmount}%増えています。すごいですね！その調子です。`;
  }
};

const getCommentTitle: GetCommentTitle = () => '1週間お疲れさまでした！';

export const AdvisorComment = ({
  Base,
  transitions,
  userName,
}: {
  Base: ReportBaseProps;
  transitions: AssetHistoryItemsProps[];
  userName: string;
}) => {
  const calcAvatorIconType: CalcAvatarIconType = totalAssetRate => {
    if (totalAssetRate > 0) {
      return 'good';
    } else if (totalAssetRate === 0) {
      return 'average';
    } else {
      return 'bad';
    }
  };

  const {
    From,
    DiffStockRate,
    DiffMarginRate,
    DiffMarginProfitOrLossRate,
    DiffUSStockRate,
    DiffInvestmentTrustRate,
    DiffRoboAdvisorRate,
    DiffUnlistedStockRate,
    DiffEtfEtnRate,
    DiffForeignEtfEtnRate,
    DiffReitRate,
    DiffForeignReitRate,
    DiffBondRate,
    DiffForeignBondRate,
    DiffEbRate,
    DiffForeignEbRate,
    DiffRealEstateRate,
    DiffCashRate,
    DiffOtherRate,
    TotalAssetAmount,
    DiffTotalAssetAmount,
    DiffTotalAssetRate,
  } = Base;
  const from = yyyymmddToDate(From.toString()) || new Date();
  const month = from.getMonth() + 1;

  if (
    DiffStockRate === null ||
    DiffMarginRate === null ||
    DiffMarginProfitOrLossRate === null ||
    DiffUSStockRate === null ||
    DiffInvestmentTrustRate === null ||
    DiffRoboAdvisorRate === null ||
    DiffUnlistedStockRate === null ||
    DiffEtfEtnRate === null ||
    DiffForeignEtfEtnRate === null ||
    DiffReitRate === null ||
    DiffForeignReitRate === null ||
    DiffBondRate === null ||
    DiffForeignBondRate === null ||
    DiffEbRate === null ||
    DiffForeignEbRate === null ||
    DiffRealEstateRate === null ||
    DiffCashRate === null ||
    DiffOtherRate === null ||
    TotalAssetAmount === null ||
    DiffTotalAssetAmount === null ||
    DiffTotalAssetRate === null
  )
    return <AdvisorCommentInternal {...{ userName }} />;

  const changeStockRate = transitions.every(
    transition =>
      transition.stockAmount === null || transition.stockAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffStockRate);

  const changeUnlistedStockRate = transitions.every(
    transition =>
      transition.unlistedStockAmount === null ||
      transition.unlistedStockAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffUnlistedStockRate);

  const changeInvestmentTrustRate = transitions.every(
    transition =>
      transition.investmentTrustAmount === null ||
      transition.investmentTrustAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffInvestmentTrustRate);

  const changeUSStockRate = transitions.every(
    transition =>
      transition.uSStockAmount === null || transition.uSStockAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffUSStockRate);

  const changeEtfEtnRate = transitions.every(
    transition =>
      transition.etfEtnAmount === null || transition.etfEtnAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffEtfEtnRate);

  const changeForeignEtfEtnRate = transitions.every(
    transition =>
      transition.foreignEtfEtnAmount === null ||
      transition.foreignEtfEtnAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffForeignEtfEtnRate);

  const changeReitRate = transitions.every(
    transition => transition.reitAmount === null || transition.reitAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffReitRate);

  const changeForeignReitRate = transitions.every(
    transition =>
      transition.foreignReitAmount === null ||
      transition.foreignReitAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffForeignReitRate);

  const changeBondRate = transitions.every(
    transition => transition.bondAmount === null || transition.bondAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffBondRate);

  const changeForeignBondRate = transitions.every(
    transition =>
      transition.foreignBondAmount === null ||
      transition.foreignBondAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffForeignBondRate);

  const changeEbRate = transitions.every(
    transition => transition.ebAmount === null || transition.ebAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffEbRate);

  const changeForeignEbRate = transitions.every(
    transition =>
      transition.foreignEbAmount === null || transition.foreignEbAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffForeignEbRate);

  const changeRealEstateRate = transitions.every(
    transition =>
      transition.realEstateAmount === null || transition.realEstateAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffRealEstateRate);

  const changeRoboRate = transitions.every(
    transition =>
      transition.roboAdvisorAmount === null ||
      transition.roboAdvisorAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffRoboAdvisorRate);

  const changeMarginRate = transitions.every(
    transition =>
      transition.marginAmount === null || transition.marginAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffMarginProfitOrLossRate);

  const changeCashRate = transitions.every(
    transition => transition.cashAmount === null || transition.cashAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffCashRate);

  const changeOtherRate = transitions.every(
    transition =>
      transition.otherAmount === null || transition.otherAmount === 0
  )
    ? null
    : addPercentAndDeltaVerboseFormat(DiffOtherRate);

  const changeTotalAssetRate = transitions.every(
    transition =>
      transition.totalAssetAmount === null || transition.totalAssetAmount === 0
  )
    ? null
    : commaFormatPercentage(DiffTotalAssetRate, {
        numberOfDecimals: 2,
      });

  const avatarIconType =
    DiffTotalAssetRate === null ? null : calcAvatorIconType(DiffTotalAssetRate);

  return (
    <AdvisorCommentInternal
      {...{
        month,
        comparisonData: [
          { name: '国内株式', change: changeStockRate },
          { name: '未上場株式', change: changeUnlistedStockRate },
          { name: '投資信託', change: changeInvestmentTrustRate },
          { name: '外国株式', change: changeUSStockRate },
          { name: '国内ETF・ETN', change: changeEtfEtnRate },
          { name: '外国ETF・ETN', change: changeForeignEtfEtnRate },
          { name: '国内REIT', change: changeReitRate },
          { name: '外国REIT', change: changeForeignReitRate },
          { name: '国内債券', change: changeBondRate },
          { name: '外国債券', change: changeForeignBondRate },
          { name: '国内EB', change: changeEbRate },
          { name: '外国EB', change: changeForeignEbRate },
          { name: '不動産', change: changeRealEstateRate },
          { name: 'ロボアド', change: changeRoboRate },
          { name: '信用取引', change: changeMarginRate },
          { name: '預金・MRF', change: changeCashRate },
          { name: 'その他', change: changeOtherRate },
        ],
        changeTotalAssetRate,
        avatarIconType,
        userName,
        getCommentContent,
        getCommentTitle,
      }}
    />
  );
};
